import { Delete, Edit, Save } from "@mui/icons-material";
import { Box, IconButton, Stack, TextField, Typography } from "@mui/material";
import useCreateOrUpdateEntityNote from "hooks/api/useCreateOrUpdateEntityNote";
import useDeleteEntityNote from "hooks/api/useDeleteEntityNote";
import useCurrentUser from "hooks/useCurrentUser";
import { DateTime } from "luxon";
import { useState } from "react";
import { standardBorder } from "theme/base/borders";
import { borderedWithWhiteBackground } from "theme/base/containers";
import NoteDto from "types/NoteDto";

interface Props {
  note: NoteDto;
}

export default function Note({ note }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState(note.content);
  const { id } = useCurrentUser();
  const { deleteEntityNote } = useDeleteEntityNote();
  const { createOrUpdateEntityNote } = useCreateOrUpdateEntityNote(
    note.entityType,
    note.entityId,
  );

  return (
    <Box
      sx={{
        position: "relative",
        ...standardBorder,
        p: 2,
        backgroundColor: "rgba(0,0,0,.05)",
      }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={1}>
          <Typography fontWeight="bold">Posted By:</Typography>
          <Typography>
            {note.firstName} {note.lastName}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ pr: 3.5 }}>
          <Typography fontWeight="bold">On:</Typography>
          <Typography>
            {note.createdOn
              ? ` ${DateTime.fromMillis(note.createdOn).toLocaleString()}`
              : ""}
          </Typography>
        </Stack>
      </Stack>
      {note.createdById === id ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            multiline
            disabled={!isEditing}
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            sx={{ mt: 0 }}
          />
          <Stack>
            {isEditing ? (
              <IconButton
                sx={{ p: 0 }}
                onClick={() => {
                  const newNote = { ...note, content: noteText };
                  createOrUpdateEntityNote(newNote);
                  setIsEditing(false);
                }}
              >
                <Save sx={{ fontSize: 16 }} />
              </IconButton>
            ) : (
              <IconButton sx={{ p: 0 }} onClick={() => setIsEditing(true)}>
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Stack>
        </Stack>
      ) : (
        <Box mt={1} sx={borderedWithWhiteBackground}>
          {noteText}
        </Box>
      )}
      {note.createdById === id && (
        <IconButton
          sx={{
            p: 0,
            position: "absolute",
            top: -8,
            right: 0,
          }}
          onClick={() =>
            deleteEntityNote(note.entityType, note.entityId, note.id)
          }
        >
          <Delete sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}
